<template>
   <div class="signup-form">
      <!-- registration form + T&Cs -->
      <form @submit.prevent>
         <div v-if="showLoginWithGoogle" data-test="continue-with-google">
            <!-- sign-up with google button -->
            <GoogleLoginButton @google-click="onSignUpWithGoogle">
               {{ $t("authentication.continue_google") }}
            </GoogleLoginButton>

            <!-- OAuth error message -->
            <div v-if="oAuthErrorMessage" class="oauth-err-block">
               <SnAlert
                  data-cy="oauth-error"
                  :message="oAuthErrorMessage"
                  id="oauth-validation-message"
                  theme="danger"
                  size="small"
                  class="validation-alert"
                  icon="warning-circle-filled"
               />
            </div>

            <div class="signup-alt-divider">
               <div class="divide"></div>
               <span>or</span>
            </div>
         </div>

         <!-- Email input -->
         <SnTextLine
            data-test="email-input"
            id="email"
            v-model.trim="state.email"
            redBorderError
            iconError
            :label="$t('validation.email.value')"
            :placeholder="$t('validation.email.placeholder')"
            :error="emailHelperData"
            size="large"
            @keyup.enter="onSignUp"
         />

         <!-- Portal Ts&Cs & Privacy policy -->
         <AgreeSignupPolicy
            class="signup-policy"
            :signupPolicy="signupPolicy"
            :showError="showSignupPolicyError"
            @change="updateAgreeSignupPolicy"
         />

         <!-- Submit -->
         <LoadingButton
            :is-loading="state.isSubmitting"
            class="submit-btn"
            data-cy="signup-email-button"
            @click="onSignUp"
         >
            {{ $t("authentication.continu") }}
         </LoadingButton>

         <!-- Comms information (to be options in future) -->
         <AgreeCommsPolicy :communicationsPolicy="communicationsPolicy" />
      </form>
   </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useAppStore } from "@/stores/appStore";
import { trackSignUpStarted } from "@/js/services/metaPixel.service";
import UsersApi from "@/data/api-layer/usersApi";
import { keysToSnake } from "@/js/helpers/object.helper";
import SnTextLine from "@sano-ui-library/src/ui/Form/SnTextLine/SnTextLine.vue";
import SnAlert from "@sano-ui-library/src/ui/SnAlert/SnAlert.vue";
import { addLocaleToRedirectPath } from "@/router/routes-config";

// declarations
// -----------------------------------------
const $emit = defineEmits(["error", "is-completed"]);
const i18n = useI18n();
const { t: $t } = i18n;
const appStore = useAppStore();

const props = defineProps<{
   signupPolicy?: any;
   communicationsPolicy?: any;
   showLoginWithGoogle: boolean;
   oauthError: string | null;
   redirect: string | null;
}>();

// state
const state = reactive({
   isSubmitting: false,
   email: "",
   agreedToSignupPolicy: false,
});

const $v: any = useVuelidate(
   {
      // email validation in this case also checks for '<' and '>' characters
      email: {
         required,
         email,
      },
      agreedToSignupPolicy: {
         mustBeTrue: (input) => !!input,
      },
   },
   state
);

// computed
// -----------------------------------------
const emailHelperData = computed(() => {
   if ($v.value.email.$errors.length === 0) return null;

   if ($v.value.email.$errors[0].$validator === "required") {
      return $t("validation.email.required");
   } else if ($v.value.email.$errors[0].$validator === "email") {
      return $t("validation.email.invalid");
   }

   return null;
});

const showSignupPolicyError = computed(() => $v?.value?.agreedToSignupPolicy?.$errors?.length > 0);

const oAuthErrorMessage = computed(() => {
   if (props.oauthError) {
      let msg = $t("validation.oauth.error");
      if (props.oauthError === "oauth_account_not_linked") msg = $t("validation.oauth.account_not_linked");
      return msg;
   }
   return null;
});

// methods
// -----------------------------------------
function updateAgreeSignupPolicy(checked) {
   state.agreedToSignupPolicy = !!checked;
}

function getLocalStorageParams() {
   // utm_source, utm_medium, utm_campaign, utm_content
   const visitSiteParams = keysToSnake(appStore.readVisitSiteParams);
   // registration_code, invitation_code, study_site_code
   const visitSignupParams = appStore.readVisitSignupParams;
   const { studyId } = appStore.readAnalyticsData;

   return { visitSiteParams, visitSignupParams, studyId };
}

const onSignUpWithGoogle = async () => {
   if (import.meta.env.SSR) return;

   // Check that the Ts & Cs have been agreed to with Google sign up also
   $v.value.agreedToSignupPolicy.$touch();
   if ($v.value.agreedToSignupPolicy.$error) return;

   let fullApiPath = "";
   if (import.meta.env.MODE !== "production") {
      // local development (requires full path because the server port is different)
      fullApiPath = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_API_PATH}`;
   } else {
      // production (API endpoint is the same as the base URL)
      fullApiPath = `${import.meta.env.VITE_APP_API_PATH}`;
   }

   // Include relevant params in the url.
   // currently, this are validated after returning from Google auth
   let gLoginParams = "client=participant&action=sign-up&has_accepted_portal_terms=true";

   const { visitSiteParams, visitSignupParams, studyId } = getLocalStorageParams();

   // For the google login endpoint, all visitSite/visitSignup params should be included
   const mergedParams = { ...visitSiteParams, ...visitSignupParams };

   // Convert keys to snake_case for BE
   const snakeCaseParams = keysToSnake(mergedParams);

   // Append each param to gLoginParams
   for (const [key, value] of Object.entries(snakeCaseParams)) {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
         gLoginParams += `&${key}=${encodeURIComponent(value.toString())}`;
      }
   }

   // The email+pw endpoint includes redirect and study_id (used for analytics) in the object "signup_url_query_params"
   // In this case, redirect_path needs to be a separate param, but "signup_params" should be included as a base64 string
   // and can include more params in future if needed
   const signupParams: { study_id?: string } = {};
   if (studyId) signupParams.study_id = studyId;
   gLoginParams += `&signup_params=${encodeURIComponent(btoa(JSON.stringify(signupParams)))}`;

   const redirect = props.redirect || "/home";
   if (redirect) gLoginParams += `&redirect_path=${encodeURIComponent(addLocaleToRedirectPath(redirect, i18n))}`;

   const locale_id = i18n.locale.value;
   if (locale_id) gLoginParams += `&locale_id=${locale_id}`;

   // The email+pw route can track the signup started event and clear web storage once a success response is received from the BE
   // In the google login case, the event is tracked when returning to the app - App.vue checkRegistrationSuccessParams()
   window.location.assign(`${fullApiPath}/oauth/authorize?${gLoginParams}`);
};

function onSignUp() {
   // touch the input to trigger validation
   $v.value.$touch();

   // if there is an error, including the case where Ts and Cs are not checked, return
   if ($v.value.$error) return;

   // no errors, so set loading to true and make the request sending through required parameters
   state.isSubmitting = true;

   const { visitSiteParams, visitSignupParams, studyId } = getLocalStorageParams();

   // define params (see register method from api.js)
   const postParams = {
      email: state.email,
      registration_source: "register",
      has_accepted_portal_terms: true,
      query_parameters: visitSiteParams,
      survey_response: undefined,
      user_role: null,
      registration_code: visitSignupParams.registrationCode,
      invitation_code: visitSignupParams.invitationCode,
      study_site_code: visitSignupParams.studySiteCode,
      // if 'redirect' is among the query parameters, add redirect to the request parameters
      signup_url_query_params: {
         ...(props.redirect && { redirect: props.redirect }),
         study_id: studyId,
      },
   };

   UsersApi.startSignupProcess(postParams)
      .then((response) => {
         // track meta pixel event for signup started (if there is a study_id in the query params)
         if (appStore.readAnalyticsData.studyId) {
            // Check if 'study_id' is not undefined or empty
            trackSignUpStarted(appStore.readAnalyticsData.studyId);
         }

         setTimeout(() => {
            state.isSubmitting = false;
            $emit("is-completed", state.email);

            // delete visit query params from local storage
            appStore.deleteVisitWebStorage();
         }, 300);
      })
      .catch((error) => {
         state.isSubmitting = false;
         let message = "Invalid StudySite identifier";
         if (error.response.data?.error === "Registration code invalid") message = "Invalid registration code";
         else if (error.response.data?.error === "Invitation code invalid") message = "Invalid invitation code";

         $emit("error", message);
      });
}
</script>

<style scoped lang="css">
.signup-alt-divider {
   display: flex;
   justify-content: center;
   margin: var(--size-base-24) 0;
   position: relative;
}

.signup-alt-divider span {
   background-color: var(--white);
   display: block;
   font-size: var(--size-text-small);
   line-height: 1.2;
   position: relative;
   padding: 0 var(--size-base-16);
}

.signup-alt-divider .divide {
   position: absolute;
   top: 50%;
   left: 0;
   right: 0;
   height: 1px;
   background-color: var(--grey-20);
}

.oauth-err-block {
   padding-block-start: var(--size-base-8);
   text-align: left;
   font-weight: var(--weight-base);
}

.signup-policy {
   margin-block-start: var(--size-base-32);
}

.submit-btn {
   margin-block-start: var(--size-base-16);
   margin-block-end: var(--size-base-32);
   width: 100%;
}

.signup-form {
   font-weight: var(--weight-base);
   position: inherit;
   width: 100%;
   color: var(--grey-80);
}
</style>
